/**
 * InformeConsultivo — vista post-reunión (DEC-005, DEC-013).
 *
 * Permite al comercial:
 *  - Editar el diagnóstico IA generado.
 *  - Añadir notas "Qué aportamos" por área (DEC-013).
 *  - Persistir en /api/reuniones/:id (campos diagnostico_ia.texto_editado, notas_por_eje).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Save, Loader2, FileText, Sparkles, Package, ChevronRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const EJES = [
  { k: "seguridad", label: "Seguridad y Riesgo" },
  { k: "combustible", label: "Combustible y Conducción" },
  { k: "operativa", label: "Operativa y Control" },
  { k: "mantenimiento", label: "Mantenimiento" },
  { k: "tic", label: "TIC y Digitalización" },
  { k: "sostenibilidad", label: "Sostenibilidad y CAES" },
];

function precargarNotas(reunion) {
  // Si el comercial ya escribió notas, NO machacar
  const existentes = reunion?.notas_por_eje || {};
  if (Object.keys(existentes).some((k) => existentes[k] && existentes[k].trim())) {
    return existentes;
  }
  // No hay notas → intentar precargar desde diagnostico_ia
  const diag = reunion?.diagnostico_ia;
  if (!diag) return existentes;
  const out = { ...existentes };
  // Intentar parsear el JSON de problemas guardado en `recomendacion` por ReunionConsultiva.generarDiagnostico
  try {
    if (diag.recomendacion) {
      const problemas = JSON.parse(diag.recomendacion);
      if (problemas && typeof problemas === "object") {
        for (const [eje, items] of Object.entries(problemas)) {
          if (!Array.isArray(items) || items.length === 0) continue;
          if (out[eje] && out[eje].trim()) continue;
          out[eje] = items
            .map((it) => {
              const p = (it.problema || "").trim();
              const s = (it.solucion || "").trim();
              return s ? (p ? `• ${p}\n  → ${s}` : `• ${s}`) : (p ? `• ${p}` : "");
            })
            .filter(Boolean)
            .join("\n");
        }
      }
    }
  } catch {
    // JSON corrupto → ignorar y caer al fallback de resumen
  }
  // Fallback: si tras la precarga por_eje aún no hay nada y existe resumen, poner resumen en seguridad
  const algunaNotaConContenido = Object.values(out).some((v) => v && v.trim());
  if (!algunaNotaConContenido && diag.resumen) {
    out.seguridad = diag.resumen;
  }
  return out;
}

export default function InformeConsultivo({ reunion, cliente, onBack, onUpdated, onCrearOferta }) {
  const [diagText, setDiagText] = useState(
    reunion?.diagnostico_ia?.texto_editado || reunion?.diagnostico_ia?.recomendacion || reunion?.diagnostico_ia?.resumen || ""
  );
  const [notas, setNotas] = useState(() => precargarNotas(reunion));
  const [saving, setSaving] = useState(false);
  const [creandoOferta, setCreandoOferta] = useState(false);

  // Recuento de respuestas por eje
  const respuestasPorEje = (reunion?.respuestas_cuestionario || []).reduce((acc, r) => {
    acc[r.eje] = (acc[r.eje] || 0) + 1;
    return acc;
  }, {});

  async function guardar() {
    setSaving(true);
    try {
      await authAxios.put(`${API}/reuniones/${reunion.id}`, {
        diagnostico_ia: {
          ...(reunion.diagnostico_ia || {}),
          texto_editado: diagText,
          editado_at: new Date().toISOString(),
        },
        notas_por_eje: notas,
      });
      toast.success("Informe guardado");
      onUpdated && onUpdated();
    } catch { toast.error("Error guardando"); }
    setSaving(false);
  }

  // DEC-004 Camino A · crea oferta desde la reunión, guarda antes el informe actual,
  // y navega al Constructor. Si el padre no provee onCrearOferta, hace fallback a axios directo.
  async function crearOfertaDesdeInforme() {
    if (creandoOferta) return;
    setCreandoOferta(true);
    try {
      // Guarda cambios pendientes del informe ANTES de crear la oferta
      await authAxios.put(`${API}/reuniones/${reunion.id}`, {
        diagnostico_ia: {
          ...(reunion.diagnostico_ia || {}),
          texto_editado: diagText,
          editado_at: new Date().toISOString(),
        },
        notas_por_eje: notas,
      });
      // Crea la oferta ligada a la reunión (DEC-004 Camino A · origen=reunion)
      const res = await authAxios.post(`${API}/ofertas`, {
        cliente_id: reunion.cliente_id,
        reunion_id: reunion.id,
        origen: "reunion",
      });
      // Vincula la reunión a la oferta creada (cierre del puente)
      await authAxios.put(`${API}/reuniones/${reunion.id}`, {
        oferta_id: res.data.id,
        estado: "convertida",
      });
      toast.success("Oferta creada desde la reunión");
      // Si el padre sabe navegar a la oferta, que lo haga. Si no, vuelve atrás.
      if (onCrearOferta) onCrearOferta(res.data);
      else { onUpdated && onUpdated(); onBack && onBack(); }
    } catch (e) {
      toast.error("Error creando oferta: " + (e?.response?.data?.detail || e.message || ""));
    }
    setCreandoOferta(false);
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} data-testid="informe-back" className="p-2 hover:bg-slate-100 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2"><FileText className="w-5 h-5" /> Informe consultivo</h1>
              <p className="text-xs text-slate-500">{cliente?.empresa} · reunión del {new Date(reunion.created_at).toLocaleDateString("es-ES")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!reunion.oferta_id && (
              <button onClick={crearOfertaDesdeInforme} disabled={creandoOferta || saving} data-testid="informe-crear-oferta"
                className="text-sm px-4 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50 flex items-center gap-2"
                title="Crea una oferta nueva ligada a esta reunión (DEC-004 Camino A)">
                {creandoOferta ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                Crear oferta con estos datos <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {reunion.oferta_id && (
              <span className="text-xs px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full font-medium">
                ✓ Oferta ya creada
              </span>
            )}
            <button onClick={guardar} disabled={saving} data-testid="informe-guardar"
              className="text-sm px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar informe
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6 space-y-4">
        {/* Resumen */}
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold mb-3">Resumen de la reunión</h2>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Metric label="Estado" value={reunion.estado} />
            <Metric label="Respuestas" value={(reunion.respuestas_cuestionario || []).length} />
            <Metric label="Diagnóstico IA" value={reunion.diagnostico_ia ? "generado" : "sin generar"} />
          </div>
        </div>

        {/* IMPL-035 (2026-05-12) · Diagnóstico IA renderizado en markdown (ya no editable como textarea
            porque la tabla se rompe; el comercial añade notas libres en el bloque inferior). */}
        <div className="bg-white rounded-xl border p-5" data-testid="informe-diagnostico">
          <h2 className="font-semibold mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-violet-500" /> Diagnóstico IA
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            Análisis generado a partir de las respuestas del cuestionario y los datos de flota.
          </p>
          {reunion.diagnostico_ia?.resumen ? (
            <div
              className="prose prose-sm max-w-none prose-p:my-2 prose-strong:text-slate-900 prose-headings:text-slate-900 prose-li:my-0.5 prose-table:my-3 prose-th:bg-violet-50 prose-th:text-slate-800 prose-th:font-bold prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:align-top prose-table:border prose-table:border-slate-200 prose-th:border prose-th:border-slate-200 prose-td:border prose-td:border-slate-200"
              data-testid="informe-diagnostico-markdown"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{reunion.diagnostico_ia.resumen}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Sin diagnóstico generado todavía.</p>
          )}
        </div>

        {/* Notas libres del comercial (sustituye a las 6 textareas DEC-013 por una única) */}
        <div className="bg-white rounded-xl border p-5">
          <h2 className="font-semibold mb-2">Notas del comercial</h2>
          <p className="text-xs text-slate-500 mb-2">
            Anotaciones libres que añade el comercial al informe (no se envían a la IA).
          </p>
          <textarea
            value={diagText}
            onChange={(e) => setDiagText(e.target.value)}
            rows={6}
            data-testid="informe-diag-textarea"
            placeholder="Observaciones del comercial, contexto adicional, recordatorios para la oferta..."
            className="w-full px-3 py-2 border rounded text-sm font-sans focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
      </main>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="p-3 bg-slate-50 rounded">
      <p className="text-[10px] uppercase text-slate-500">{label}</p>
      <p className="text-base font-bold text-slate-900">{value}</p>
    </div>
  );
}
